import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/firestore';
import { Observable, Subject, BehaviorSubject, fromEvent, merge } from 'rxjs';
import { takeUntil, debounceTime, throttleTime, filter } from 'rxjs/operators';
import firebase from 'firebase/app';

import {
  UserActivitySession,
  UserActivity,
  PageVisit,
  ActivityType,
  DeviceType,
  FormInteraction,
  FormInteractionType,
  PageInteraction,
  InteractionType,
  ApiCall,
  GeoLocation,
  UserBehaviorAnalytics,
  ActivityDashboardData,
  SecurityEvent,
  SecurityEventType,
  SecuritySeverity,
  EngagementLevel,
  RiskIndicator
} from '../models/audit.model';

@Injectable({
  providedIn: 'root'
})
export class UserActivityService implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Firestore collections
  private sessionsCollection: AngularFirestoreCollection<UserActivitySession>;
  private activitiesCollection: AngularFirestoreCollection<UserActivity>;
  private pageVisitsCollection: AngularFirestoreCollection<PageVisit>;
  private securityEventsCollection: AngularFirestoreCollection<SecurityEvent>;

  // Current session tracking
  private currentSession: UserActivitySession | null = null;
  private currentPageVisit: PageVisit | null = null;
  private sessionStartTime: Date = new Date();
  private lastActivityTime: Date = new Date();

  // Activity metrics
  private mouseClicks: number = 0;
  private keyStrokes: number = 0;
  private scrollDistance: number = 0;
  private pageInteractions: PageInteraction[] = [];
  private formInteractions: FormInteraction[] = [];
  private apiCalls: ApiCall[] = [];

  // Real-time activity stream
  private activityStream$ = new BehaviorSubject<UserActivity[]>([]);

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private router: Router
  ) {
    this.initializeCollections();
    this.initializeTracking();
  }

  ngOnDestroy(): void {
    this.endCurrentSession();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeCollections(): void {
    this.sessionsCollection = this.afs.collection<UserActivitySession>('user_activity_sessions');
    this.activitiesCollection = this.afs.collection<UserActivity>('user_activities');
    this.pageVisitsCollection = this.afs.collection<PageVisit>('page_visits');
    this.securityEventsCollection = this.afs.collection<SecurityEvent>('security_events');
  }

  private initializeTracking(): void {
    // Start session on authentication
    this.afAuth.authState
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          this.startNewSession();
        } else {
          this.endCurrentSession();
        }
      });

    // Track navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.trackPageVisit(event.url);
      });

    // Track user interactions
    this.initializeInteractionTracking();

    // Auto-save session data periodically
    setInterval(() => {
      if (this.currentSession) {
        this.saveSessionData();
      }
    }, 30000); // Every 30 seconds

    // Track inactivity
    this.trackUserInactivity();
  }

  private async startNewSession(): Promise<void> {
    try {
      const user = await this.afAuth.currentUser;
      if (!user) return;

      const sessionId = this.afs.createId();
      const deviceInfo = await this.getDeviceInfo();
      const location = await this.getGeoLocation();

      this.currentSession = {
        id: sessionId,
        user_id: user.uid,
        user_email: user.email,
        session_id: sessionId,
        start_time: firebase.firestore.Timestamp.now(),
        ip_address: await this.getClientIP(),
        user_agent: navigator.userAgent,
        device_type: deviceInfo.type,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        location: location,
        activities: [],
        pages_visited: [],
        total_actions: 0,
        is_active: true,
        last_activity: firebase.firestore.Timestamp.now()
      };

      this.sessionStartTime = new Date();
      this.lastActivityTime = new Date();
      this.resetMetrics();

      await this.sessionsCollection.doc(sessionId).set(this.currentSession);

      console.log('🚀 New user activity session started:', sessionId);
    } catch (error) {
      console.error('Error starting new session:', error);
    }
  }

  private async endCurrentSession(): Promise<void> {
    if (!this.currentSession) return;

    try {
      // End current page visit
      if (this.currentPageVisit) {
        await this.endCurrentPageVisit();
      }

      // Update session with final data
      const now = firebase.firestore.Timestamp.now();
      const durationMinutes = Math.round((new Date().getTime() - this.sessionStartTime.getTime()) / 60000);

      const sessionUpdate: Partial<UserActivitySession> = {
        end_time: now,
        duration_minutes: durationMinutes,
        total_actions: this.currentSession.total_actions,
        is_active: false,
        last_activity: now
      };

      await this.sessionsCollection.doc(this.currentSession.id).update(sessionUpdate);

      console.log('🏁 User activity session ended:', this.currentSession.id);
      this.currentSession = null;
    } catch (error) {
      console.error('Error ending session:', error);
    }
  }

  private async trackPageVisit(url: string): Promise<void> {
    if (!this.currentSession) return;

    try {
      // End previous page visit
      if (this.currentPageVisit) {
        await this.endCurrentPageVisit();
      }

      // Start new page visit
      const visitId = this.afs.createId();
      const pageTitle = document.title;

      this.currentPageVisit = {
        id: visitId,
        session_id: this.currentSession.session_id,
        page_url: url,
        page_title: pageTitle,
        entry_time: firebase.firestore.Timestamp.now(),
        time_spent_seconds: 0,
        scroll_depth_percentage: 0,
        bounce: false,
        previous_page: this.currentPageVisit?.page_url,
        interactions: []
      };

      await this.pageVisitsCollection.doc(visitId).set(this.currentPageVisit);

      // Log activity
      await this.logActivity({
        action_type: ActivityType.CLICK,
        action_details: `Navigated to ${pageTitle}`,
        resource_accessed: url,
        time_spent_seconds: 0
      });

    } catch (error) {
      console.error('Error tracking page visit:', error);
    }
  }

  private async endCurrentPageVisit(): Promise<void> {
    if (!this.currentPageVisit) return;

    try {
      const now = firebase.firestore.Timestamp.now();
      const entryTime = this.currentPageVisit.entry_time as firebase.firestore.Timestamp;
      const timeSpent = Math.round((now.toMillis() - entryTime.toMillis()) / 1000);

      const visitUpdate: Partial<PageVisit> = {
        exit_time: now,
        time_spent_seconds: timeSpent,
        scroll_depth_percentage: this.calculateScrollDepth(),
        bounce: timeSpent < 5, // Less than 5 seconds = bounce
        interactions: [...this.pageInteractions]
      };

      await this.pageVisitsCollection.doc(this.currentPageVisit.id).update(visitUpdate);

      // Reset for next page
      this.pageInteractions = [];
    } catch (error) {
      console.error('Error ending page visit:', error);
    }
  }

  private initializeInteractionTracking(): void {
    if (typeof window === 'undefined') return;

    // Mouse clicks
    fromEvent(document, 'click')
      .pipe(
        throttleTime(100),
        takeUntil(this.destroy$)
      )
      .subscribe((event: MouseEvent) => {
        this.trackClick(event);
      });

    // Key strokes
    fromEvent(document, 'keydown')
      .pipe(
        throttleTime(100),
        takeUntil(this.destroy$)
      )
      .subscribe((event: KeyboardEvent) => {
        this.trackKeyStroke(event);
      });

    // Scroll tracking
    fromEvent(window, 'scroll')
      .pipe(
        throttleTime(250),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.trackScroll();
      });

    // Form interactions
    fromEvent(document, 'focus', { capture: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: FocusEvent) => {
        this.trackFormFocus(event);
      });

    fromEvent(document, 'input', { capture: true })
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe((event: Event) => {
        this.trackFormInput(event);
      });

    // Mouse movement and hovers
    fromEvent(document, 'mouseover')
      .pipe(
        throttleTime(1000),
        takeUntil(this.destroy$)
      )
      .subscribe((event: MouseEvent) => {
        this.trackHover(event);
      });
  }

  private trackClick(event: MouseEvent): void {
    this.mouseClicks++;
    this.updateLastActivity();

    const element = event.target as HTMLElement;
    const interaction: PageInteraction = {
      element_id: element.id || element.className || element.tagName,
      element_type: element.tagName,
      interaction_type: InteractionType.CLICK,
      timestamp: firebase.firestore.Timestamp.now(),
      coordinates: { x: event.clientX, y: event.clientY }
    };

    this.pageInteractions.push(interaction);

    this.logActivity({
      action_type: ActivityType.CLICK,
      action_details: `Clicked on ${element.tagName}`,
      resource_accessed: element.id || element.className,
      mouse_clicks: 1
    });
  }

  private trackKeyStroke(event: KeyboardEvent): void {
    this.keyStrokes++;
    this.updateLastActivity();

    // Don't log sensitive keys
    const sensitiveKeys = ['Enter', 'Tab', 'Shift', 'Control', 'Alt', 'Meta'];
    if (!sensitiveKeys.includes(event.key)) {
      this.logActivity({
        action_type: ActivityType.FORM_INPUT,
        action_details: 'Keyboard input',
        keyboard_strokes: 1
      });
    }
  }

  private trackScroll(): void {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.scrollDistance += Math.abs(scrollTop - (this.scrollDistance || 0));
    this.updateLastActivity();

    this.logActivity({
      action_type: ActivityType.SCROLL,
      action_details: 'Page scroll',
      scroll_distance: scrollTop
    });
  }

  private trackFormFocus(event: FocusEvent): void {
    const element = event.target as HTMLInputElement;
    if (!element || !['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) return;

    const interaction: FormInteraction = {
      form_id: element.form?.id || 'unknown',
      form_name: element.form?.name || element.name || 'unknown',
      field_name: element.name || element.id || 'unknown',
      interaction_type: FormInteractionType.FOCUS,
      timestamp: firebase.firestore.Timestamp.now(),
      value_changed: false
    };

    this.formInteractions.push(interaction);
  }

  private trackFormInput(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (!element || !['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) return;

    const interaction: FormInteraction = {
      form_id: element.form?.id || 'unknown',
      form_name: element.form?.name || element.name || 'unknown',
      field_name: element.name || element.id || 'unknown',
      interaction_type: FormInteractionType.INPUT,
      timestamp: firebase.firestore.Timestamp.now(),
      value_changed: true
    };

    this.formInteractions.push(interaction);
  }

  private trackHover(event: MouseEvent): void {
    const element = event.target as HTMLElement;

    this.logActivity({
      action_type: ActivityType.HOVER,
      action_details: `Hovered over ${element.tagName}`,
      resource_accessed: element.id || element.className
    });
  }

  private trackUserInactivity(): void {
    // Track periods of inactivity
    setInterval(() => {
      const now = new Date();
      const inactiveTime = now.getTime() - this.lastActivityTime.getTime();
      const inactiveMinutes = Math.round(inactiveTime / 60000);

      if (inactiveMinutes >= 5 && this.currentSession?.is_active) {
        this.logActivity({
          action_type: ActivityType.BLUR,
          action_details: `User inactive for ${inactiveMinutes} minutes`,
          time_spent_seconds: inactiveMinutes * 60
        });

        // Mark session as inactive if user is away for more than 30 minutes
        if (inactiveMinutes >= 30) {
          this.markSessionInactive();
        }
      }
    }, 60000); // Check every minute
  }

  private async markSessionInactive(): Promise<void> {
    if (!this.currentSession) return;

    try {
      await this.sessionsCollection.doc(this.currentSession.id).update({
        is_active: false,
        last_activity: firebase.firestore.Timestamp.now()
      });

      this.currentSession.is_active = false;
    } catch (error) {
      console.error('Error marking session inactive:', error);
    }
  }

  private async logActivity(activityData: Partial<UserActivity>): Promise<void> {
    if (!this.currentSession) return;

    try {
      const activityId = this.afs.createId();
      const activity: UserActivity = {
        id: activityId,
        session_id: this.currentSession.session_id,
        timestamp: firebase.firestore.Timestamp.now(),
        mouse_clicks: this.mouseClicks,
        keyboard_strokes: this.keyStrokes,
        scroll_distance: this.scrollDistance,
        form_interactions: [...this.formInteractions],
        api_calls: [...this.apiCalls],
        ...activityData
      };

      await this.activitiesCollection.doc(activityId).set(activity);

      // Update current session
      this.currentSession.total_actions = (this.currentSession.total_actions || 0) + 1;
      this.currentSession.last_activity = firebase.firestore.Timestamp.now();

      // Update real-time stream
      const currentActivities = this.activityStream$.value;
      this.activityStream$.next([activity, ...currentActivities.slice(0, 49)]); // Keep last 50

    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }

  private updateLastActivity(): void {
    this.lastActivityTime = new Date();

    if (this.currentSession && !this.currentSession.is_active) {
      // Reactivate session if user becomes active again
      this.currentSession.is_active = true;
      this.sessionsCollection.doc(this.currentSession.id).update({
        is_active: true,
        last_activity: firebase.firestore.Timestamp.now()
      });
    }
  }

  private resetMetrics(): void {
    this.mouseClicks = 0;
    this.keyStrokes = 0;
    this.scrollDistance = 0;
    this.pageInteractions = [];
    this.formInteractions = [];
    this.apiCalls = [];
  }

  private async saveSessionData(): Promise<void> {
    if (!this.currentSession) return;

    try {
      const now = firebase.firestore.Timestamp.now();
      const durationMinutes = Math.round((new Date().getTime() - this.sessionStartTime.getTime()) / 60000);

      const sessionUpdate: Partial<UserActivitySession> = {
        duration_minutes: durationMinutes,
        total_actions: this.currentSession.total_actions,
        last_activity: now
      };

      await this.sessionsCollection.doc(this.currentSession.id).update(sessionUpdate);
    } catch (error) {
      console.error('Error saving session data:', error);
    }
  }

  // Utility methods
  private async getDeviceInfo(): Promise<{type: DeviceType, browser: string, os: string}> {
    const userAgent = navigator.userAgent;

    // Detect device type
    let deviceType: DeviceType = DeviceType.DESKTOP;
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
      deviceType = DeviceType.TABLET;
    } else if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile|wpdesktop/i.test(userAgent)) {
      deviceType = DeviceType.MOBILE;
    }

    // Detect browser
    let browser = 'Unknown';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';

    // Detect OS
    let os = 'Unknown';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    return { type: deviceType, browser, os };
  }

  private async getGeoLocation(): Promise<GeoLocation | null> {
    try {
      // Use IP-based geolocation service
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      return {
        country: data.country_name,
        region: data.region,
        city: data.city,
        timezone: data.timezone,
        latitude: data.latitude,
        longitude: data.longitude
      };
    } catch (error) {
      console.warn('Could not get geo location:', error);
      return null;
    }
  }

  private async getClientIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'unavailable';
    } catch (error) {
      console.warn('Could not get client IP:', error);
      return 'unavailable';
    }
  }

  private calculateScrollDepth(): number {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    return scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
  }

  // Public API methods
  public async trackApiCall(endpoint: string, method: string, responseTimeMs: number, statusCode: number, dataSizeBytes?: number): Promise<void> {
    const apiCall: ApiCall = {
      endpoint,
      method,
      timestamp: firebase.firestore.Timestamp.now(),
      response_time_ms: responseTimeMs,
      status_code: statusCode,
      data_size_bytes: dataSizeBytes
    };

    this.apiCalls.push(apiCall);

    await this.logActivity({
      action_type: ActivityType.SEARCH, // Or appropriate type based on API call
      action_details: `API call to ${endpoint}`,
      resource_accessed: endpoint,
      api_calls: [apiCall]
    });
  }

  public async trackCustomActivity(activityData: Partial<UserActivity>): Promise<void> {
    await this.logActivity(activityData);
  }

  public getRealTimeActivityStream(): Observable<UserActivity[]> {
    return this.activityStream$.asObservable();
  }

  public async getUserSessions(userId: string, limit: number = 50): Promise<UserActivitySession[]> {
    try {
      const snapshot = await this.sessionsCollection
        .ref
        .where('user_id', '==', userId)
        .orderBy('start_time', 'desc')
        .limit(limit)
        .get();

      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserActivitySession));
    } catch (error) {
      console.error('Error getting user sessions:', error);
      return [];
    }
  }

  public async getUserBehaviorAnalytics(userId: string, startDate: Date, endDate: Date): Promise<UserBehaviorAnalytics | null> {
    try {
      const sessions = await this.getUserSessions(userId, 1000);
      const filteredSessions = sessions.filter(session => {
        const sessionDate = session.start_time ? (session.start_time as firebase.firestore.Timestamp).toDate() : new Date();
        return sessionDate >= startDate && sessionDate <= endDate;
      });

      if (filteredSessions.length === 0) {
        return null;
      }

      // Calculate analytics
      const totalSessions = filteredSessions.length;
      const totalTimeMinutes = filteredSessions.reduce((sum, session) => sum + (session.duration_minutes || 0), 0);
      const averageSessionDuration = totalTimeMinutes / totalSessions;

      // Most active hours
      const hourCounts = new Array(24).fill(0);
      filteredSessions.forEach(session => {
        if (session.start_time) {
          const hour = (session.start_time as firebase.firestore.Timestamp).toDate().getHours();
          hourCounts[hour]++;
        }
      });
      const mostActiveHours = hourCounts
        .map((count, hour) => ({ hour, count }))
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map(item => item.hour);

      return {
        user_id: userId,
        analysis_period: { start: startDate, end: endDate },
        total_sessions: totalSessions,
        total_time_minutes: totalTimeMinutes,
        average_session_duration: averageSessionDuration,
        most_active_hours: mostActiveHours,
        most_active_days: [], // TODO: Calculate most active days
        favorite_pages: [], // TODO: Calculate from page visits
        common_actions: [], // TODO: Calculate from activities
        productivity_score: this.calculateProductivityScore(filteredSessions),
        engagement_level: this.calculateEngagementLevel(averageSessionDuration, totalSessions),
        risk_indicators: [] // TODO: Analyze for risk patterns
      };
    } catch (error) {
      console.error('Error getting user behavior analytics:', error);
      return null;
    }
  }

  private calculateProductivityScore(sessions: UserActivitySession[]): number {
    // Simple productivity score based on session duration and activity count
    if (sessions.length === 0) return 0;

    const avgDuration = sessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) / sessions.length;
    const avgActions = sessions.reduce((sum, s) => sum + (s.total_actions || 0), 0) / sessions.length;

    // Score from 0-100 based on activity level
    return Math.min(100, Math.round((avgDuration * 0.5) + (avgActions * 0.1)));
  }

  private calculateEngagementLevel(avgSessionDuration: number, totalSessions: number): EngagementLevel {
    const engagementScore = (avgSessionDuration * 0.7) + (totalSessions * 2);

    if (engagementScore >= 100) return EngagementLevel.VERY_HIGH;
    if (engagementScore >= 60) return EngagementLevel.HIGH;
    if (engagementScore >= 30) return EngagementLevel.MEDIUM;
    return EngagementLevel.LOW;
  }
}